import { useEffect, useState } from "react";
import { Pencil, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { StatusPill } from "@/components/StatusPill";
import { api, apiErr, fileUrl } from "@/lib/api";
import { fmtDate, fmtTime, fmtTemp, parseTempInput } from "@/lib/format";

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-2 text-sm last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{children}</span>
    </div>
  );
}

export function ControlDetail({ checkId, open, onClose }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!checkId || !open) { setData(null); setEditing(false); return; }
    api.get(`/temperature-checks/${checkId}`).then(({ data }) => setData(data)).catch(() => {});
  }, [checkId, open]);

  const canEdit = data && new Date(data.editable_until) > new Date();

  const save = async () => {
    const v = parseTempInput(val);
    if (v === null) { toast.error("Valor inválido"); return; }
    if (!reason.trim()) { toast.error("Indica el motivo de la corrección"); return; }
    setSaving(true);
    try {
      await api.put(`/temperature-checks/${checkId}`, { confirmed_value: v, reason });
      toast.success("Registro corregido");
      const { data: fresh } = await api.get(`/temperature-checks/${checkId}`);
      setData(fresh); setEditing(false); setReason("");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display">Detalle del control</DialogTitle></DialogHeader>
        {!data ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            {data.photo_url && (
              <img src={fileUrl(data.photo_url)} alt="evidencia" className="aspect-[4/3] w-full rounded-xl object-cover" />
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-lg font-semibold">{data.equipment_name}</p>
                <p className="text-xs text-slate-500">{data.location_name} · {data.area_name}</p>
              </div>
              <StatusPill status={data.status} size="lg" />
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
              <p className="text-xs text-slate-500">Temperatura confirmada</p>
              <p className="tnum font-display text-3xl font-semibold">{fmtTemp(data.confirmed_value, data.unit)}</p>
              <p className="text-xs text-slate-500">Rango {fmtTemp(data.min_temperature_snapshot, data.unit)}–{fmtTemp(data.max_temperature_snapshot, data.unit)}</p>
            </div>
            <div>
              <Row label="Fecha">{fmtDate(data.created_at)}</Row>
              <Row label="Hora">{fmtTime(data.created_at)}</Row>
              <Row label="Turno">{data.shift_name || "—"}</Row>
              <Row label="Usuario">{data.user_name}</Row>
              <Row label="OCR detectado">{data.ocr_value != null ? fmtTemp(data.ocr_value, data.unit) : "—"}</Row>
              <Row label="Confianza OCR">{data.ocr_confidence != null ? `${Math.round(data.ocr_confidence * 100)}%` : "—"}</Row>
              <Row label="Ingreso manual">{data.entered_manually ? "Sí" : "No"}</Row>
              <Row label="Creado offline">{data.offline_created ? "Sí" : "No"}</Row>
            </div>

            {data.revisions?.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Historial de revisiones</p>
                {data.revisions.map((r) => (
                  <div key={r.id} className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                    {fmtTemp(r.old_temperature, data.unit)} → {fmtTemp(r.new_temperature, data.unit)} · {r.reason} · {fmtTime(r.created_at)}
                  </div>
                ))}
              </div>
            )}

            {canEdit && !editing && (
              <Button data-testid="edit-check-btn" variant="outline" className="w-full rounded-xl" onClick={() => { setEditing(true); setVal(String(data.confirmed_value).replace(".", ",")); }}>
                <Pencil className="mr-2 h-4 w-4" /> Editar registro (3 min)
              </Button>
            )}
            {editing && (
              <div className="space-y-3 rounded-xl border border-slate-200 p-3">
                <div><Label className="text-xs">Nuevo valor (°{data.unit})</Label>
                  <Input data-testid="edit-value-input" value={val} onChange={(e) => setVal(e.target.value)} className="mt-1 h-11 tnum" /></div>
                <div><Label className="text-xs">Motivo</Label>
                  <Input data-testid="edit-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 h-11" placeholder="Motivo de la corrección" /></div>
                <Button data-testid="edit-save-btn" disabled={saving} onClick={save} className="w-full rounded-xl">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar corrección"}</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
